import { Button, Card, Grid, Group, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { setWhatsAppAccount } from '../../store/whatsappSlice';
import ConfirmationModal from '../Modals/ConfirmationModal';
import WhatsAppConnectButton from './WhatsAppConnectButton';

const WhatsAppSettings = ({ setIsBspWhatsappSettingsValid = () => {} }) => {
  const dispatch = useDispatch();
  const whatsappAccount = useSelector((state) => state.whatsapp.account);
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [disconnecting, setDisconnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isWabaIdValid, setIsWabaIdValid] = useState(true);
  const [isWhatsappPhoneNumberValid, setIsWhatsappPhoneNumberValid] = useState(true);

  const [connectionMethod, setConnectionMethod] = useState(whatsappAccount?.whatsappSender || 'whatsappManager');
  const [pin, setPin] = useState('');
  const [registering, setRegistering] = useState(false);
  // Synchronous in-flight guard for the disconnect. `disconnecting` drives the
  // UI (button spinner), but it updates asynchronously, so two clicks in the
  // same tick would both read the pre-render value and fire duplicate DELETEs.
  // The ref flips synchronously and blocks that race.
  const disconnectInFlightRef = useRef(false);

  const handleConnectionMethodChange = (value) => {
    setConnectionMethod(value);
    dispatch(setWhatsAppAccount({ ...whatsappAccount, whatsappSender: value }));
  };

  const handleSaveBspConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsapp/account`, {
        method: 'PUT',
        body: JSON.stringify({
          ...whatsappAccount,
          whatsappEnabled: true,
        }),
      });
      dispatch(setWhatsAppAccount(response.whatsappAccount));
    } catch (err) {
      notifications.show({
        title: 'Failed to save BSP configuration',
        message: getErrorMessage(err, 'Could not save the BSP configuration. Please try again or contact support.'),
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch(setWhatsAppAccount({ ...whatsappAccount, [name]: value }));
  };

  useEffect(() => {
    setIsBspWhatsappSettingsValid(!!whatsappAccount?.bspAuthToken && !!isWabaIdValid && !!isWhatsappPhoneNumberValid);
  }, [whatsappAccount, isWabaIdValid, isWhatsappPhoneNumberValid, setIsBspWhatsappSettingsValid]);

  const handleDisconnectWhatsApp = async () => {
    // Guard against a double-submit: without it a second click (button or modal
    // Confirm) fires a duplicate DELETE while the first is still in flight.
    if (disconnectInFlightRef.current) {
      return;
    }
    disconnectInFlightRef.current = true;
    setDisconnecting(true);
    setShowDisconnectModal(false);
    try {
      await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsapp/account`, {
        method: 'DELETE',
      });
      dispatch(setWhatsAppAccount(null));
    } catch (err) {
      // Surface the backend's message (e.g. Meta refusing the WABA unsubscribe)
      // via a toast - previously it only reached the browser console/network
      // tab, so a failed disconnect looked like a silent no-op.
      notifications.show({
        title: 'Failed to disconnect WhatsApp',
        message: getErrorMessage(err, 'Could not disconnect WhatsApp from Releva. Please try again or contact support.'),
        color: 'danger',
      });
    } finally {
      disconnectInFlightRef.current = false;
      setDisconnecting(false);
    }
  };

  const handleRegisterPhone = async () => {
    setRegistering(true);
    try {
      await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsapp/register-phone`, {
        method: 'POST',
        body: JSON.stringify({
          phoneNumberId: whatsappAccount?.whatsappPhoneNumberId,
          pin,
        }),
      });
      setPin('');
      dispatch(setWhatsAppAccount({ ...whatsappAccount, isPhoneRegistered: true }));
    } catch (err) {
      notifications.show({
        title: 'Failed to register phone number',
        message: getErrorMessage(err, 'Could not register the phone number. Please try again or contact support.'),
        color: 'danger',
      });
    } finally {
      setRegistering(false);
    }
  };

  const isIdValid = (value) => {
    return /^\d{15,16}$/.test(value);
  };

  const handleValidation = (event) => {
    const inputElement = event.target;
    if (inputElement.name === 'whatsappAccountId') {
      setIsWabaIdValid(isIdValid(inputElement.value));
    }
    if (inputElement.name === 'whatsappPhoneNumberId') {
      setIsWhatsappPhoneNumberValid(isIdValid(inputElement.value));
    }
  };

  const isBspConnected = whatsappAccount?.whatsappEnabled && whatsappAccount?.whatsappSender === 'bsp';

  const isWhatsAppManagerConnected =
    whatsappAccount?.whatsappEnabled && whatsappAccount?.whatsappSender === 'whatsappManager';

  const showBspForm = connectionMethod === 'bsp' && !whatsappAccount?.whatsappEnabled;

  return (
    <Stack gap="md">
      {/* Connection Method Selection - only when not connected */}
      {!whatsappAccount?.whatsappEnabled && (
        <Card padding="md" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>Connection Method</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <SegmentedControl
                  value={connectionMethod ?? ''}
                  onChange={handleConnectionMethodChange}
                  data={[
                    {
                      label: 'WhatsApp Manager',
                      value: 'whatsappManager',
                    },
                    { label: 'Business Solution Provider', value: 'bsp' },
                  ]}
                />
              </Grid.Col>
            </Grid>

            {connectionMethod === 'whatsappManager' && <WhatsAppConnectButton />}
          </Stack>
        </Card>
      )}

      {/* BSP Form - shown when selecting BSP or when connected via BSP */}
      {(showBspForm || isBspConnected) && (
        <Card padding="md" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>Auth Token</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  required
                  name="bspAuthToken"
                  placeholder="Authentication token"
                  value={whatsappAccount?.bspAuthToken || ''}
                  onChange={handleChange}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>WhatsApp Business Account ID</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  required
                  name="whatsappAccountId"
                  placeholder="WhatsApp Business Account ID"
                  value={whatsappAccount?.whatsappAccountId || ''}
                  onChange={(e) => {
                    handleChange(e);
                    handleValidation(e);
                  }}
                  error={
                    !isWabaIdValid ? 'Please enter a valid WhatsApp Business Account ID (15-16 numeric digits).' : null
                  }
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>WhatsApp Phone Number ID</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  required
                  name="whatsappPhoneNumberId"
                  placeholder="Phone Number ID"
                  value={whatsappAccount?.whatsappPhoneNumberId || ''}
                  onChange={(e) => {
                    handleChange(e);
                    handleValidation(e);
                  }}
                  error={
                    !isWhatsappPhoneNumberValid
                      ? 'Please enter a valid WhatsApp Phone Number ID (15-16 numeric digits).'
                      : null
                  }
                />
              </Grid.Col>
            </Grid>
            <Group justify="flex-end">
              <Button
                onClick={handleSaveBspConfig}
                loading={saving}
                disabled={
                  !whatsappAccount?.bspAuthToken ||
                  !isWabaIdValid ||
                  !isWhatsappPhoneNumberValid ||
                  !whatsappAccount?.whatsappAccountId ||
                  !whatsappAccount?.whatsappPhoneNumberId
                }
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* BSP Disconnect Button */}
      {isBspConnected && (
        <Card padding="md" withBorder>
          <Group justify="center">
            <Button variant="light" color="red" onClick={() => setShowDisconnectModal(true)} loading={disconnecting}>
              Disconnect WhatsApp
            </Button>
          </Group>
        </Card>
      )}

      {isWhatsAppManagerConnected && (
        <Card padding="md" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>WhatsApp Business Account</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput value={whatsappAccount?.whatsappAccountId || ''} readOnly />
              </Grid.Col>
            </Grid>
            {whatsappAccount?.whatsappPhoneNumberId && (
              <Grid>
                <Grid.Col span={4}>
                  <Text fw={500}>Phone Number</Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <TextInput value={whatsappAccount?.whatsappPhoneNumberId || ''} readOnly />
                </Grid.Col>
              </Grid>
            )}
          </Stack>
        </Card>
      )}

      {/* Phone Registration - shown when connected via WhatsApp Manager and not yet registered */}
      {isWhatsAppManagerConnected && !whatsappAccount?.isPhoneRegistered && (
        <Card padding="md" withBorder>
          <Stack gap="md">
            <Text fw={500}>Register Phone Number</Text>
            <Text size="sm" c="dimmed">
              After connecting via WhatsApp Embedded Signup, you need to register your phone number with a 6-digit PIN
              to enable messaging. This PIN is used for two-factor authentication.
            </Text>
            <Grid>
              <Grid.Col span={4}>
                <Text fw={500}>6-Digit PIN</Text>
              </Grid.Col>
              <Grid.Col span={8}>
                <TextInput
                  placeholder="Enter 6-digit PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                />
              </Grid.Col>
            </Grid>
            <Group justify="flex-end">
              <Button
                onClick={handleRegisterPhone}
                loading={registering}
                disabled={pin.length !== 6 || !whatsappAccount?.whatsappPhoneNumberId}
              >
                Register Phone
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* WhatsApp Manager Disconnect Button */}
      {isWhatsAppManagerConnected && (
        <Card padding="md" withBorder>
          <Group justify="center">
            <Button variant="light" color="red" onClick={() => setShowDisconnectModal(true)} loading={disconnecting}>
              Disconnect WhatsApp
            </Button>
          </Group>
        </Card>
      )}

      <ConfirmationModal
        show={showDisconnectModal}
        close={() => setShowDisconnectModal(false)}
        confirm={handleDisconnectWhatsApp}
        message="Are you sure you want to disconnect WhatsApp? This will remove all WhatsApp configuration."
      />
    </Stack>
  );
};

export default WhatsAppSettings;
